/**
 * useYearlyReport Hook
 * Custom hook for fetching yearly report data using TanStack Query.
 */

import { useQuery } from '@tanstack/react-query';
import { reportApi } from '../api/transactionApi';

export const reportKeys = {
  all: ['reports'] as const,
  yearly: (year: number) => [...reportKeys.all, 'yearly', year] as const,
};

/**
 * Hook for fetching yearly report data
 */
export function useYearlyReport(year: number) {
  const query = useQuery({
    queryKey: reportKeys.yearly(year),
    queryFn: () => reportApi.getYearlyReport(year),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  return {
    report: query.data?.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for exporting yearly report as CSV
 */
export function useExportReport() {
  const downloadCsv = async (year: number) => {
    try {
      const blob = await reportApi.exportCsv(year);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pigmoneyhelp-relatorio-${year}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to export report:', error);
      throw error;
    }
  };

  return { downloadCsv };
}
